/**
 * The three exteriors, built from arithmetic.
 *
 * ## Why this file exists
 *
 * Until M7 there was exactly one airframe — a high-wing single assembled from eight boxes and two
 * cylinders — and all three aeroplanes wore it. That is defensible for one aeroplane and indefensible
 * for three: the fleet's whole point is that a trainer, an unlimited aerobat and a fly-by-wire fighter
 * *fly* differently, and flying them all in the same body makes the difference something you read off
 * the HUD rather than something you see. The reheat plume made it absurd — shock diamonds standing in
 * the exhaust of an aeroplane with a propeller and wing struts.
 *
 * So each aircraft gets its own geometry, selected by id, and the three are meant to be told apart in
 * silhouette:
 *
 *   - **Sparrow** — high wing, lift struts, fixed tricycle gear, a big greenhouse. Nothing about it
 *     is fast and the shape says so.
 *   - **Tumbler** — low mid-set wing with no dihedral and a symmetric section, a bubble canopy, a
 *     tailwheel, spatted main gear and an oversized rudder that runs to the bottom of the fuselage.
 *     The rudder is the give-away: an unlimited aerobat needs authority at zero airspeed.
 *   - **Vandal** — a chined nose, a ventral intake under a blended body, a cropped delta with
 *     leading-edge root extensions, a single canted fin, ventral strakes, wingtip missile rails and a
 *     petalled nozzle, whose station this airframe *declares* through {@link Airframe.nozzle} so
 *     that `AfterburnerPlume` follows the geometry rather than the geometry following the plume.
 *
 * ## Why lofted rather than assembled from `BoxGeometry`
 *
 * A wing is a tapered, swept, twisted surface with a thickness distribution, and none of those four
 * words describes a box. Boxes are why the previous airframe read as "aeroplane-shaped object" rather
 * than as an aeroplane at any distance where the silhouette is all you have — which, in a chase
 * camera at 22 m, is most of the time. {@link loft} takes a list of cross-sections placed in space and
 * skins them, so a wing is written as its root and tip sections and the taper, sweep and dihedral fall
 * out of where those sections are put. The same function skins the fuselages, which is why the
 * Vandal's body can be a chined superellipse forward and a round nozzle aft without special-casing.
 *
 * Everything here is built once at construction and never touched again, so this file is outside the
 * frame path entirely. It allocates freely; the frame does not.
 *
 * ## Triangle budget
 *
 * CLAUDE.md's ceiling is 50 000 triangles per aircraft. These come in around 3–7 k — the sections are
 * coarse because the shapes are, and the cost that matters at these sizes is draw calls, not vertices.
 * `airframes.test.ts` asserts the budget so a future detailing pass cannot quietly blow through it.
 *
 * ## These are now the fallback
 *
 * Since milestone 7.5 there are real models: `public/models/<id>/model.glb`, baked from CC-BY
 * sources by `tools/prep-aircraft.py` and loaded on selection by `render/aircraftModel.ts`. This
 * geometry is what is drawn when a model is missing or fails to load, which is a case that has to
 * keep working — so it is still built for every aircraft, still tested, and still built to exactly
 * the contract the models are baked to. See "The real aircraft models" in the README.
 *
 * `updateFixed` / `updateFrame`: neither. Construction only. The one moving part is the propeller
 * disc, which `FlightScene` spins, and the gear, which it retracts.
 */

import {
  BufferAttribute,
  BufferGeometry,
  CircleGeometry,
  Color,
  CylinderGeometry,
  Group,
  Mesh,
  SphereGeometry,
  TorusGeometry,
  Vector2,
  Vector3,
  type Material,
} from 'three';
import { MeshStandardNodeMaterial } from 'three/webgpu';
import { DEG2RAD } from '../core/math.js';

/** What a built airframe hands back to the scene that owns it. */
export interface Airframe {
  /** Everything drawn from outside the aeroplane. Hidden in the cockpit view. */
  group: Group;
  /**
   * The propeller disc, spun about its local Z. Null on the jet, which has none — and a null the
   * caller must handle rather than an invisible stand-in, because "there is no propeller" and "the
   * propeller is not being drawn" are different facts and only one of them is true of the Vandal.
   */
  propeller: Mesh | null;
  /**
   * The parts that retract, and how far they travel. Empty when the gear is fixed.
   *
   * Retraction is modelled as a translation into the body rather than as a hinge, which is a
   * simplification and a visible one only in the two seconds it is moving. What it buys is that the
   * gear can be driven by the flight model's `gearFraction` — one number, already smoothed, already
   * carrying the travel time from the aircraft's own JSON — instead of by a second animation system
   * that would have to agree with it.
   */
  retractingParts: readonly RetractingPart[];
  /** Where this airframe's reheat plume starts. Null unless it has an afterburner. */
  nozzle: NozzleAnchor | null;
  /** Disposed with the scene. */
  geometries: readonly BufferGeometry[];
  materials: readonly Material[];
}

export interface RetractingPart {
  mesh: Mesh;
  /** Position with the gear down [m]. */
  down: Vector3;
  /** Position with the gear up [m]. */
  up: Vector3;
}

/**
 * Where a jet's reheat plume starts, and how wide the pipe it comes out of is.
 *
 * **The exterior owns this, and the effect reads it** — which is the opposite of how it worked
 * until M7.6. `main.ts` used to hard-code `[0, 0.1, 4.6]`, so the *model* had to be positioned to
 * put its nozzle on that number, and the Vandal paid for it with an origin 68 % aft along its
 * airframe where an F-16's centre of gravity is nearer 55 %. Inverting it costs nothing and buys
 * back the origin: each exterior states where its own nozzle is, and the plume goes there.
 *
 * Null for anything without an afterburner, which is every aeroplane here but one. Null rather
 * than a zero vector on purpose — "this aircraft has no nozzle" and "this aircraft's nozzle is at
 * the origin" are different facts, and a plume at the origin would come out of the cockpit.
 */
export interface NozzleAnchor {
  /** Nozzle exit centre, render space, relative to the aircraft origin [m]. */
  positionM: Vector3;
  /** Nozzle exit radius [m]. The plume must never be drawn wider than this. */
  exitRadiusM: number;
}

/**
 * Build the exterior for an aircraft id.
 *
 * Unknown ids get the trainer, for the same reason `aircraftById` does: a bad id must not be able to
 * produce an aeroplane with no body.
 */
/**
 * Span correction for the two aircraft whose fallback borrows another's builder [dimensionless].
 *
 * Each is the aeroplane's own `wingSpanM` over the span of the shape it borrows: the Learjet's
 * 12.04 m over the fighter's 9.96, the warbird's 9.80 m over the trainer's 11.00.
 */
const FALLBACK_SPAN_SCALE: Readonly<Record<string, number>> = Object.freeze({
  learjet: 12.04 / 9.96,
  warbird: 9.8 / 11.0,
});

export function buildAirframe(id: string): Airframe {
  const builder = new AirframeBuilder();
  switch (id) {
    case 'tumbler':
      buildTumbler(builder);
      break;
    case 'vandal':
      buildVandal(builder);
      break;
    // The business jet falls back to the fighter's shape rather than the trainer's, and the reason
    // is one detail: `buildSparrow` gives its aeroplane a propeller. A jet whose model failed to
    // load should not appear with one spinning on the nose — a wrong silhouette reads as low
    // fidelity, a propeller on a turbofan reads as a bug.
    case 'learjet':
      buildVandal(builder);
      break;
    case 'warbird':
      buildSparrow(builder);
      break;
    default:
      buildSparrow(builder);
      break;
  }
  builder.group.name = `${id}-exterior`;
  /*
   * Scale the fallback onto the span the flight model actually flies.
   *
   * The first three aeroplanes each have their own builder authored at their own dimensions, so
   * the question never arose. The last two reuse a shape — a business jet has a fighter's
   * silhouette long before it has a trainer's, and a radial warbird has a single-engine
   * propeller's — and reusing it at the wrong size is a visibly wrong aeroplane: `buildVandal` is
   * a 9.96 m fighter and the Learjet spans 12.04. This is the load-failure fallback, so getting
   * the proportions right matters more than getting the shape exactly right.
   */
  const scale = FALLBACK_SPAN_SCALE[id];
  if (scale !== undefined) builder.group.scale.setScalar(scale);
  return builder.finish();
}

// --- the two primitives everything is made of ------------------------------------------------

/**
 * A cross-section: a closed loop of points in its own plane, plus where that plane sits.
 *
 * `points` are (u, v) in the section's plane; the section is placed at `origin` with `u` along
 * `axisU` and `v` along `axisV`. Written this way so one function skins both a fuselage (sections
 * stacked along the body axis) and a wing (sections stacked along the span) without either of them
 * needing to know about the other.
 */
interface Section {
  points: readonly Vector2[];
  origin: Vector3;
  axisU: Vector3;
  axisV: Vector3;
}

/**
 * Skin a stack of cross-sections.
 *
 * Every section must have the same number of points, ordered consistently around the loop, or the
 * skin twists. Both ends are capped with a triangle fan from the section's centroid, which is exact
 * for the convex sections used here.
 *
 * Normals come from `computeVertexNormals` rather than being derived analytically: the sections are
 * coarse enough that the averaged face normals *are* the shape's normals, and a wing whose smoothing
 * follows its facets is a wing that reads as faceted.
 */
function loft(sections: readonly Section[]): BufferGeometry {
  const ring = sections[0].points.length;
  const positions: number[] = [];
  const indices: number[] = [];

  const at = (section: Section, i: number, out: Vector3): Vector3 => {
    const p = section.points[i];
    return out
      .copy(section.origin)
      .addScaledVector(section.axisU, p.x)
      .addScaledVector(section.axisV, p.y);
  };

  const scratch = new Vector3();
  for (const section of sections) {
    for (let i = 0; i < ring; i++) {
      at(section, i, scratch);
      positions.push(scratch.x, scratch.y, scratch.z);
    }
  }

  for (let s = 0; s < sections.length - 1; s++) {
    const a = s * ring;
    const b = (s + 1) * ring;
    for (let i = 0; i < ring; i++) {
      const j = (i + 1) % ring;
      indices.push(a + i, b + i, b + j);
      indices.push(a + i, b + j, a + j);
    }
  }

  // Caps. The centroid is appended after the rings so the ring indices above stay valid.
  for (const [index, section] of [
    [0, sections[0]] as const,
    [sections.length - 1, sections[sections.length - 1]] as const,
  ]) {
    const centre = positions.length / 3;
    const sum = new Vector3();
    for (let i = 0; i < ring; i++) sum.add(at(section, i, scratch));
    sum.multiplyScalar(1 / ring);
    positions.push(sum.x, sum.y, sum.z);

    const base = index * ring;
    for (let i = 0; i < ring; i++) {
      const j = (i + 1) % ring;
      // Wound opposite ways at the two ends so both caps face outward.
      if (index === 0) indices.push(centre, base + j, base + i);
      else indices.push(centre, base + i, base + j);
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

/** Points per aerofoil contour. Even, so the leading and trailing edges land on points. */
const AEROFOIL_POINTS = 16;

/**
 * A symmetric NACA-00xx contour, closed, running over the upper surface from the leading edge to
 * the trailing edge and back along the lower.
 *
 * Symmetric for all three aeroplanes, which is right for two of them — an aerobat needs a symmetric
 * section to fly inverted as well as upright, and a fighter's wing is close enough at this scale —
 * and a simplification for the trainer, whose real section is cambered. At 11 m of span seen from
 * 22 m away, the camber is worth less than the half-millimetre of geometry it would cost.
 *
 * @param thickness Maximum thickness as a fraction of chord — the `xx` in NACA-00xx.
 */
function aerofoil(thickness: number): Vector2[] {
  const half = AEROFOIL_POINTS / 2;
  const points: Vector2[] = [];
  const yAt = (x: number): number =>
    5 *
    thickness *
    (0.2969 * Math.sqrt(x) - 0.126 * x - 0.3516 * x * x + 0.2843 * x ** 3 - 0.1015 * x ** 4);

  // Cosine spacing: points bunch at the leading edge, which is where the curvature is.
  for (let i = 0; i <= half; i++) {
    const x = 0.5 * (1 - Math.cos((Math.PI * i) / half));
    points.push(new Vector2(x, yAt(x)));
  }
  for (let i = half - 1; i > 0; i--) {
    const x = 0.5 * (1 - Math.cos((Math.PI * i) / half));
    points.push(new Vector2(x, -yAt(x)));
  }
  return points;
}

/**
 * A rounded-rectangle cross-section, as a superellipse.
 *
 * `roundness` 1 is an ellipse and 4 is very nearly a rectangle with filleted corners. It is what
 * lets one function describe the Sparrow's round fuselage, the Tumbler's slab-sided one and the
 * Vandal's chined forward body.
 */
function superellipse(halfWidth: number, halfHeight: number, roundness: number, points = 16): Vector2[] {
  const out: Vector2[] = [];
  for (let i = 0; i < points; i++) {
    const theta = (i / points) * Math.PI * 2;
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    const n = 2 / roundness;
    out.push(
      new Vector2(
        halfWidth * Math.sign(c) * Math.abs(c) ** n,
        halfHeight * Math.sign(s) * Math.abs(s) ** n,
      ),
    );
  }
  return out;
}

// --- the builder ------------------------------------------------------------------------------

/**
 * Render space, stated once because every number below depends on it: **nose is −Z, up is +Y, right
 * is +X**, metres, origin at the centre of gravity.
 */
const NOSE = -1;

interface WingOptions {
  /** Root leading-edge position [m]. */
  root: Vector3;
  rootChordM: number;
  tipChordM: number;
  /** Half-span, root to tip [m]. */
  spanM: number;
  /** Leading-edge sweep, positive aft [deg]. */
  sweepDeg?: number;
  dihedralDeg?: number;
  /** Thickness/chord ratio. */
  thickness?: number;
  /** Root incidence, positive nose-up [deg]. */
  incidenceDeg?: number;
  /** +1 for the right panel, −1 for the left. */
  side: 1 | -1;
  /** Vertical surfaces: build the panel in the XY plane instead of XZ. */
  vertical?: boolean;
}

class AirframeBuilder {
  readonly group = new Group();
  private readonly geometries: BufferGeometry[] = [];
  private readonly materials: Material[] = [];
  private readonly retracting: RetractingPart[] = [];
  private nozzle: NozzleAnchor | null = null;
  propeller: Mesh | null = null;

  material(hex: number, roughness: number, metalness = 0.1, opacity = 1): MeshStandardNodeMaterial {
    const material = new MeshStandardNodeMaterial();
    material.color = new Color(hex);
    material.roughness = roughness;
    material.metalness = metalness;
    if (opacity < 1) {
      material.transparent = true;
      material.opacity = opacity;
    }
    this.materials.push(material);
    return material;
  }

  /** Add a mesh, tracking its geometry for disposal and turning shadow casting on. */
  add(geometry: BufferGeometry, material: Material, position?: Vector3): Mesh {
    this.geometries.push(geometry);
    const mesh = new Mesh(geometry, material);
    if (position !== undefined) mesh.position.copy(position);
    mesh.castShadow = true;
    this.group.add(mesh);
    return mesh;
  }

  /**
   * A body of revolution-ish hull: superellipse stations lofted along Z.
   *
   * Stations are given nose-first. `centreY` lets the body's spine curve, which is what puts the
   * Vandal's cockpit above its intake without a second object.
   */
  body(
    material: Material,
    stations: readonly { z: number; halfWidth: number; halfHeight: number; roundness?: number; centreY?: number }[],
  ): Mesh {
    const sections = stations.map((station) => ({
      points: superellipse(
        station.halfWidth,
        station.halfHeight,
        station.roundness ?? 1,
      ),
      origin: new Vector3(0, station.centreY ?? 0, station.z),
      axisU: new Vector3(1, 0, 0),
      axisV: new Vector3(0, 1, 0),
    }));
    return this.add(loft(sections), material);
  }

  /** One lifting panel, root to tip. Call twice with `side: 1` and `side: -1` for a wing. */
  wing(material: Material, options: WingOptions): Mesh {
    const {
      root,
      rootChordM,
      tipChordM,
      spanM,
      side,
      sweepDeg = 0,
      dihedralDeg = 0,
      thickness = 0.12,
      incidenceDeg = 0,
      vertical = false,
    } = options;

    const contour = aerofoil(thickness);
    const sweep = Math.tan(sweepDeg * DEG2RAD);
    const dihedral = Math.tan(dihedralDeg * DEG2RAD);
    const incidence = incidenceDeg * DEG2RAD;

    // Two sections is a straight-tapered panel, which is what all six of these are. The chordwise
    // axis is −Z (aft is +Z, and the aerofoil's x runs leading edge to trailing edge).
    const sections: Section[] = [];
    for (const t of [0, 1]) {
      const chord = rootChordM + (tipChordM - rootChordM) * t;
      const stationSpan = spanM * t;
      const origin = new Vector3(
        root.x + side * (vertical ? 0 : stationSpan),
        root.y + (vertical ? stationSpan : dihedral * stationSpan),
        root.z + sweep * stationSpan,
      );
      // Chordwise axis, tilted by the incidence angle about the span axis.
      const axisU = vertical
        ? new Vector3(0, 0, chord)
        : new Vector3(0, -Math.sin(incidence) * chord, Math.cos(incidence) * chord);
      // Thickness axis: up for a horizontal surface, sideways for a fin.
      const axisV = vertical
        ? new Vector3(chord, 0, 0)
        : new Vector3(0, Math.cos(incidence) * chord, Math.sin(incidence) * chord);
      sections.push({ points: contour, origin, axisU, axisV });
    }
    return this.add(loft(sections), material);
  }

  /** A wheel on a leg. Returns the pair as one group-parented mesh list. */
  gearLeg(
    legMaterial: Material,
    tyreMaterial: Material,
    at: Vector3,
    legLengthM: number,
    wheelRadiusM: number,
    retractTo?: Vector3,
  ): void {
    const leg = this.add(
      new CylinderGeometry(0.055, 0.045, legLengthM, 8),
      legMaterial,
      new Vector3(at.x, at.y - legLengthM / 2, at.z),
    );
    const wheel = this.add(
      new CylinderGeometry(wheelRadiusM, wheelRadiusM, wheelRadiusM * 0.7, 14),
      tyreMaterial,
      new Vector3(at.x, at.y - legLengthM, at.z),
    );
    wheel.rotation.z = Math.PI / 2;

    if (retractTo !== undefined) {
      for (const part of [leg, wheel]) {
        this.retracting.push({
          mesh: part,
          down: part.position.clone(),
          up: part.position.clone().add(retractTo),
        });
      }
    }
  }

  /** Declare where this airframe's nozzle exit is, for whatever draws the plume. */
  setNozzle(positionM: Vector3, exitRadiusM: number): void {
    this.nozzle = { positionM: positionM.clone(), exitRadiusM };
  }

  finish(): Airframe {
    return {
      group: this.group,
      propeller: this.propeller,
      retractingParts: this.retracting,
      nozzle: this.nozzle,
      geometries: this.geometries,
      materials: this.materials,
    };
  }
}

/** A translucent disc for a turning propeller, plus the spinner in front of it. */
function propellerDisc(builder: AirframeBuilder, at: Vector3, radiusM: number, spinnerMaterial: Material): void {
  const bladeMaterial = builder.material(0x14181d, 0.6, 0.1, 0.3);
  const spinner = builder.add(
    new CylinderGeometry(0.02, 0.3, 0.62, 14),
    spinnerMaterial,
    new Vector3(at.x, at.y, at.z - 0.31),
  );
  spinner.rotation.x = Math.PI / 2;

  const disc = builder.add(new CircleGeometry(radiusM, 28), bladeMaterial, at);
  disc.castShadow = false;
  builder.propeller = disc;
}

// --- Sparrow: the trainer ---------------------------------------------------------------------

/**
 * A 180 hp high-wing single, 11 m of span and about 8.3 m long.
 *
 * The three things that make it read as a trainer rather than as "small aeroplane" are the wing
 * sitting on top of the cabin, the lift struts under it, and the fixed nosewheel hanging in the
 * breeze. All three are drag, and all three are why it is the slow one.
 */
function buildSparrow(builder: AirframeBuilder): void {
  const skin = builder.material(0xe8eaed, 0.42, 0.12);
  const trim = builder.material(0x1f4f8f, 0.35, 0.2);
  const dark = builder.material(0x14181d, 0.6);
  const glass = builder.material(0x2b3f52, 0.08, 0.3, 0.55);

  builder.body(skin, [
    { z: NOSE * 3.9, halfWidth: 0.24, halfHeight: 0.26, centreY: 0.16 },
    { z: NOSE * 3.1, halfWidth: 0.46, halfHeight: 0.46, centreY: 0.14 },
    { z: NOSE * 1.7, halfWidth: 0.58, halfHeight: 0.62, centreY: 0.1 },
    { z: NOSE * 0.2, halfWidth: 0.6, halfHeight: 0.66, roundness: 1.3, centreY: 0.08 },
    { z: 1.5, halfWidth: 0.5, halfHeight: 0.52, centreY: 0.12 },
    { z: 3.1, halfWidth: 0.3, halfHeight: 0.34, centreY: 0.22 },
    { z: 4.4, halfWidth: 0.13, halfHeight: 0.2, centreY: 0.3 },
  ]);

  // The greenhouse: a trainer's defining feature is that you can see out of it.
  const cabin = builder.body(glass, [
    { z: NOSE * 1.75, halfWidth: 0.5, halfHeight: 0.3, roundness: 1.6, centreY: 0.6 },
    { z: NOSE * 0.9, halfWidth: 0.57, halfHeight: 0.4, roundness: 1.8, centreY: 0.64 },
    { z: 0.5, halfWidth: 0.56, halfHeight: 0.38, roundness: 1.8, centreY: 0.62 },
    { z: 1.35, halfWidth: 0.4, halfHeight: 0.24, roundness: 1.5, centreY: 0.58 },
  ]);
  cabin.castShadow = false;

  for (const side of [1, -1] as const) {
    builder.wing(skin, {
      root: new Vector3(0.55 * side, 1.02, NOSE * 0.85),
      rootChordM: 1.6,
      tipChordM: 1.15,
      spanM: 4.95,
      dihedralDeg: 1.7,
      thickness: 0.15,
      incidenceDeg: 1.5,
      side,
    });
    // The lift strut, from the spar to the lower fuselage longeron.
    const strut = builder.add(
      new CylinderGeometry(0.05, 0.05, 2.15, 6),
      trim,
      new Vector3(side * 1.55, 0.5, NOSE * 0.2),
    );
    strut.rotation.z = side * 27 * DEG2RAD;
    strut.rotation.x = 8 * DEG2RAD;

    builder.wing(skin, {
      root: new Vector3(0.15 * side, 0.28, 3.75),
      rootChordM: 1.0,
      tipChordM: 0.62,
      spanM: 1.65,
      sweepDeg: 6,
      thickness: 0.1,
      side,
    });
  }

  // Fin and rudder — modest, because a trainer's is.
  builder.wing(trim, {
    root: new Vector3(0, 0.34, 3.35),
    rootChordM: 1.5,
    tipChordM: 0.78,
    spanM: 1.35,
    sweepDeg: 32,
    thickness: 0.11,
    side: 1,
    vertical: true,
  });

  propellerDisc(builder, new Vector3(0, 0.16, NOSE * 4.25), 0.95, trim);

  // Fixed tricycle gear: nose leg forward, mains under the cabin.
  builder.gearLeg(trim, dark, new Vector3(0, -0.12, NOSE * 2.6), 0.78, 0.24);
  for (const side of [1, -1] as const) {
    builder.gearLeg(trim, dark, new Vector3(side * 1.22, -0.36, 0.32), 0.72, 0.3);
  }
}

// --- Tumbler: the aerobat ---------------------------------------------------------------------

/**
 * A 300 hp unlimited aerobat: 8 m of span, short, and built around a rudder.
 *
 * Everything about the shape follows from what it has to do. The wing is mid-set with **no
 * dihedral**, because dihedral is roll stability and roll stability is exactly what a 366°/s roll
 * rate does not want. The section is symmetric so inverted flight is the same flight. The rudder is
 * enormous and runs below the fuselage as well as above it, which is what gives a tail-slide
 * something to bite on. And the gear is a taildragger with spatted mains, because it is 12 kg
 * lighter than a nosewheel and every kilogram is a tenth of a g.
 */
function buildTumbler(builder: AirframeBuilder): void {
  const skin = builder.material(0xd7261f, 0.36, 0.18);
  const flash = builder.material(0xf2f4f7, 0.3, 0.2);
  const dark = builder.material(0x14181d, 0.55);
  const glass = builder.material(0x243544, 0.06, 0.35, 0.5);

  builder.body(skin, [
    { z: NOSE * 3.5, halfWidth: 0.3, halfHeight: 0.32, centreY: 0.05 },
    { z: NOSE * 2.7, halfWidth: 0.52, halfHeight: 0.55, centreY: 0.02 },
    { z: NOSE * 1.2, halfWidth: 0.56, halfHeight: 0.62, roundness: 1.4, centreY: 0 },
    { z: 0.4, halfWidth: 0.48, halfHeight: 0.56, roundness: 1.5, centreY: 0.02 },
    { z: 1.9, halfWidth: 0.3, halfHeight: 0.38, roundness: 1.4, centreY: 0.1 },
    { z: 3.4, halfWidth: 0.12, halfHeight: 0.22, centreY: 0.18 },
  ]);

  // A bubble canopy sitting proud of the spine — an aerobat is flown by looking out of the top of it.
  const canopy = builder.body(glass, [
    { z: NOSE * 1.35, halfWidth: 0.33, halfHeight: 0.2, roundness: 1.4, centreY: 0.55 },
    { z: NOSE * 0.55, halfWidth: 0.42, halfHeight: 0.34, roundness: 1.5, centreY: 0.56 },
    { z: 0.45, halfWidth: 0.38, halfHeight: 0.3, roundness: 1.5, centreY: 0.54 },
    { z: 1.15, halfWidth: 0.2, halfHeight: 0.14, centreY: 0.48 },
  ]);
  canopy.castShadow = false;

  for (const side of [1, -1] as const) {
    // No dihedral, symmetric section, near-constant chord: a wing with no opinion about which way
    // up it is.
    builder.wing(skin, {
      root: new Vector3(0.4 * side, -0.12, NOSE * 1.15),
      rootChordM: 1.62,
      tipChordM: 1.18,
      spanM: 3.6,
      dihedralDeg: 0,
      thickness: 0.14,
      side,
    });
    builder.wing(flash, {
      root: new Vector3(0.14 * side, 0.12, 2.55),
      rootChordM: 1.05,
      tipChordM: 0.6,
      spanM: 1.42,
      sweepDeg: 8,
      thickness: 0.1,
      side,
    });
  }

  // The rudder, above and below. The lower half is the part that makes this aeroplane's shape.
  builder.wing(flash, {
    root: new Vector3(0, 0.2, 2.2),
    rootChordM: 1.5,
    tipChordM: 0.95,
    spanM: 1.5,
    sweepDeg: 20,
    thickness: 0.1,
    side: 1,
    vertical: true,
  });
  builder.wing(flash, {
    root: new Vector3(0, -0.18, 2.5),
    rootChordM: 1.0,
    tipChordM: 0.62,
    spanM: -0.42,
    sweepDeg: -14,
    thickness: 0.1,
    side: 1,
    vertical: true,
  });

  propellerDisc(builder, new Vector3(0, 0.05, NOSE * 3.85), 0.99, flash);

  // Taildragger: spatted mains forward of the CG, and a tailwheel on the sternpost.
  for (const side of [1, -1] as const) {
    builder.gearLeg(flash, dark, new Vector3(side * 0.95, -0.42, NOSE * 0.85), 0.5, 0.26);
    const spat = builder.add(
      new SphereGeometry(0.34, 10, 8),
      flash,
      new Vector3(side * 0.95, -0.92, NOSE * 0.85),
    );
    spat.scale.set(0.5, 0.75, 1.25);
  }
  builder.gearLeg(dark, dark, new Vector3(0, -0.1, 3.35), 0.28, 0.11);
}

// --- Vandal: the fighter ----------------------------------------------------------------------

/**
 * Where this airframe draws its nozzle, and therefore where its plume starts.
 *
 * It is a station on *this* geometry rather than a number handed down from `main.ts`: the plume
 * reads {@link Airframe.nozzle} and goes wherever the exterior says. The built airframe and the
 * baked glTF each answer for themselves and neither has to agree with the other — which is what
 * lets the real model sit on its own centre of gravity. See {@link NozzleAnchor}.
 */
const VANDAL_NOZZLE = /* #__PURE__ */ new Vector3(0, 0.1, 4.6);
const VANDAL_NOZZLE_EXIT_RADIUS_M = 0.55;

/**
 * A single-engine fly-by-wire fighter: 9.96 m of span, about 15 m long, and shaped like one.
 *
 * The features that carry the silhouette, in the order they are visible from behind:
 *
 *   - a **petalled nozzle** at {@link VANDAL_NOZZLE}, which is where this airframe tells the reheat
 *     plume to start. This is the whole reason the milestone touched the models at all: the shock
 *     diamonds were coming out of the back of a high-wing trainer.
 *   - a **ventral intake** under a blended forebody. Nothing else on the aeroplane says "jet" as
 *     immediately, and putting it under the body rather than on the sides is what makes it this
 *     jet rather than a generic one.
 *   - a **cropped delta** with leading-edge root extensions running forward to the canopy, one
 *     canted fin, and two ventral strakes.
 *   - **wingtip missile rails**, which is where the four IR rounds `weapons/` fires are carried.
 */
function buildVandal(builder: AirframeBuilder): void {
  const skin = builder.material(0x6f7681, 0.5, 0.35);
  const shade = builder.material(0x545b66, 0.55, 0.35);
  const dark = builder.material(0x1a1d22, 0.5, 0.4);
  const glass = builder.material(0x30415a, 0.05, 0.6, 0.45);
  const nozzleMetal = builder.material(0x3b3f45, 0.28, 0.85);

  /*
   * The body: a chined forebody that fairs into a wide, flat-bottomed centre section and back out to
   * the nozzle. `roundness` does the work — 2.4 at the chine stations is nearly a rounded rectangle
   * lying on its side, which is what a fighter's radar nose actually looks like from in front.
   */
  builder.body(skin, [
    { z: NOSE * 7.4, halfWidth: 0.08, halfHeight: 0.08, centreY: 0.05 },
    { z: NOSE * 6.3, halfWidth: 0.42, halfHeight: 0.32, roundness: 2.2, centreY: 0.02 },
    { z: NOSE * 5.0, halfWidth: 0.66, halfHeight: 0.5, roundness: 2.4, centreY: 0 },
    { z: NOSE * 3.4, halfWidth: 0.78, halfHeight: 0.66, roundness: 2.2, centreY: 0.02 },
    { z: NOSE * 1.2, halfWidth: 0.9, halfHeight: 0.76, roundness: 1.8, centreY: 0.04 },
    { z: 0.8, halfWidth: 0.88, halfHeight: 0.78, roundness: 1.6, centreY: 0.06 },
    { z: 2.6, halfWidth: 0.74, halfHeight: 0.72, roundness: 1.3, centreY: 0.08 },
    { z: 4.0, halfWidth: 0.6, halfHeight: 0.6, centreY: 0.1 },
    { z: 4.55, halfWidth: 0.55, halfHeight: 0.55, centreY: 0.1 },
  ]);

  /*
   * The ventral intake, hung under the forebody with a splitter plate standing it off the skin.
   * The gap is not decoration: an intake flush against the fuselage would swallow the boundary
   * layer, which is why real ones stand off, and at this scale the visible slot is most of what
   * makes the shape legible.
   */
  const intake = builder.body(shade, [
    { z: NOSE * 4.2, halfWidth: 0.62, halfHeight: 0.34, roundness: 2.6, centreY: -0.78 },
    { z: NOSE * 3.2, halfWidth: 0.72, halfHeight: 0.42, roundness: 2.4, centreY: -0.72 },
    { z: NOSE * 1.0, halfWidth: 0.76, halfHeight: 0.46, roundness: 2.0, centreY: -0.58 },
    { z: 1.2, halfWidth: 0.6, halfHeight: 0.4, centreY: -0.4 },
  ]);
  intake.castShadow = true;
  // The intake lip, as a ring standing proud of the duct mouth.
  const lip = builder.add(
    new TorusGeometry(0.6, 0.06, 8, 18),
    dark,
    new Vector3(0, -0.78, NOSE * 4.3),
  );
  lip.scale.set(1, 0.58, 1);

  // The canopy, well forward, sitting on the spine ahead of the wing.
  const canopy = builder.body(glass, [
    { z: NOSE * 5.5, halfWidth: 0.28, halfHeight: 0.16, roundness: 1.5, centreY: 0.6 },
    { z: NOSE * 4.6, halfWidth: 0.44, halfHeight: 0.34, roundness: 1.6, centreY: 0.66 },
    { z: NOSE * 3.5, halfWidth: 0.46, halfHeight: 0.36, roundness: 1.6, centreY: 0.7 },
    { z: NOSE * 2.3, halfWidth: 0.34, halfHeight: 0.22, centreY: 0.64 },
  ]);
  canopy.castShadow = false;

  for (const side of [1, -1] as const) {
    // Cropped delta: heavy sweep, small tip chord, thin section.
    builder.wing(skin, {
      root: new Vector3(0.8 * side, -0.05, NOSE * 2.2),
      rootChordM: 4.6,
      tipChordM: 1.15,
      spanM: 4.18,
      sweepDeg: 40,
      dihedralDeg: -1,
      thickness: 0.05,
      side,
    });

    // The leading-edge root extension: a long, shallow strake running forward to under the canopy.
    // It is what makes the fighter's planform continuous from nose to tail instead of a wing bolted
    // to a tube, and on the real aeroplane it is what keeps the fin flying at 25° of alpha.
    builder.wing(shade, {
      root: new Vector3(0.72 * side, 0.1, NOSE * 5.1),
      rootChordM: 3.0,
      tipChordM: 2.4,
      spanM: 0.55,
      sweepDeg: 12,
      thickness: 0.04,
      side,
    });

    // All-moving tailplane, aft and below the wing plane.
    builder.wing(shade, {
      root: new Vector3(0.62 * side, -0.06, 2.9),
      rootChordM: 1.9,
      tipChordM: 0.7,
      spanM: 1.85,
      sweepDeg: 38,
      thickness: 0.05,
      side,
    });

    // Ventral strake, under the tail. Directional stability at high alpha, and unmistakable.
    builder.wing(dark, {
      root: new Vector3(0.5 * side, -0.6, 3.0),
      rootChordM: 1.5,
      tipChordM: 0.5,
      spanM: -0.55,
      sweepDeg: 42,
      thickness: 0.08,
      side: 1,
      vertical: true,
    }).position.set(side * 0.5, 0, 0);

    // Wingtip rail, and a round on it.
    const rail = builder.add(
      new CylinderGeometry(0.05, 0.05, 1.1, 6),
      dark,
      new Vector3(side * 4.98, 0.02, NOSE * 0.6),
    );
    rail.rotation.x = Math.PI / 2;
    const round = builder.add(
      new CylinderGeometry(0.08, 0.08, 2.6, 8),
      builder.material(0xd9dbe0, 0.4, 0.2),
      new Vector3(side * 4.98, -0.22, NOSE * 0.35),
    );
    round.rotation.x = Math.PI / 2;
  }

  // One fin, canted outboard a couple of degrees so it is not a plane of symmetry.
  const fin = builder.wing(skin, {
    root: new Vector3(0, 0.55, 1.5),
    rootChordM: 3.3,
    tipChordM: 1.3,
    spanM: 2.4,
    sweepDeg: 42,
    thickness: 0.06,
    side: 1,
    vertical: true,
  });
  fin.rotation.z = -2 * DEG2RAD;

  /*
   * The nozzle: a convergent-divergent petal ring, and the origin the plume is told to start from.
   *
   * Modelled as a short reversed cone so the exit is wider than the throat, plus a ring of petals,
   * because the shock diamonds are a consequence of imperfect expansion and the geometry that
   * causes it should be visible. The drawn exit radius and the radius handed to
   * {@link AirframeBuilder.setNozzle} are the same constant, so a plume wider than the pipe it
   * comes out of — the one detail that would give the whole thing away — is not expressible.
   */
  const nozzle = builder.add(
    new CylinderGeometry(VANDAL_NOZZLE_EXIT_RADIUS_M, 0.46, 0.9, 16, 1, true),
    nozzleMetal,
    new Vector3(VANDAL_NOZZLE.x, VANDAL_NOZZLE.y, VANDAL_NOZZLE.z - 0.4),
  );
  nozzle.rotation.x = Math.PI / 2;
  builder.setNozzle(VANDAL_NOZZLE, VANDAL_NOZZLE_EXIT_RADIUS_M);
  const throat = builder.add(
    new CylinderGeometry(0.4, 0.4, 0.1, 16),
    dark,
    new Vector3(VANDAL_NOZZLE.x, VANDAL_NOZZLE.y, VANDAL_NOZZLE.z - 0.85),
  );
  throat.rotation.x = Math.PI / 2;

  /*
   * Retractable gear. Nose leg into the intake trunk, mains into the wing roots — the same places
   * a real one puts them, and the reason the retracted positions are up *and* inboard.
   */
  builder.gearLeg(shade, dark, new Vector3(0, -0.6, NOSE * 4.6), 0.95, 0.26, new Vector3(0, 1.1, 0.9));
  for (const side of [1, -1] as const) {
    builder.gearLeg(
      shade,
      dark,
      new Vector3(side * 1.15, -0.7, 0.3),
      1.0,
      0.34,
      new Vector3(-side * 0.7, 1.15, 0),
    );
  }
}
